import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ImageOff } from 'lucide-react';

export const ImageSlideshow = ({ images, title }) => {
    const { t } = useTranslation()
    const [currentIndex, setCurrentIndex] = useState(0);
    const [failedImages, setFailedImages] = useState({});

    useEffect(() => {
        if (!images || images.length <= 1) return;

        const interval = setInterval(() => {
            setCurrentIndex((prev) => (prev + 1) % images.length);
        }, 5000);

        return () => clearInterval(interval);
    }, [JSON.stringify(images)]);

    // Empty state: no images configured at all
    if (!images || images.length === 0) {
        return (
            <div className="w-full h-full flex flex-col items-center justify-center gap-6 text-center p-8">
                <div className="p-6 rounded-3xl bg-white/5 border border-white/10 shadow-inner">
                    <ImageOff size={56} className="text-white/20" />
                </div>
                <div>
                    <p className="text-2xl font-bold text-white/40">{t('presentation_page.img_not_found')}</p>
                    <p className="text-sm text-white/20 mt-2">Voeg een afbeeldings-URL toe in de slide-editor</p>
                </div>
            </div>
        );
    }

    return (
        <div className="text-center w-full h-full flex flex-col items-center justify-center z-10 relative">
            {title && (
                <h2 className="text-4xl font-bold mb-6 text-white drop-shadow-md absolute top-12 z-20">
                    {title}
                </h2>
            )}

            {/* Image Container */}
            <div className="relative w-full h-full flex items-center justify-center p-8">
                {images.map((img, idx) => (
                    failedImages[idx] ? (
                        // Premium dark fallback for a broken/missing URL
                        <div
                            key={idx}
                            className={`absolute inset-8 rounded-2xl flex flex-col items-center justify-center gap-4 bg-white/5 border border-white/10 backdrop-blur-sm transition-opacity duration-1000 ${idx === currentIndex ? 'opacity-100 z-10' : 'opacity-0 z-0'}`}
                        >
                            <div className="p-4 rounded-2xl bg-white/5 border border-white/10">
                                <ImageOff size={48} className="text-white/25" />
                            </div>
                            <p className="text-white/40 font-bold text-xl">{t('presentation_page.img_not_found')}</p>
                            <p className="text-white/20 text-xs font-mono max-w-xs truncate px-4">{img}</p>
                        </div>
                    ) : (
                        <img
                            key={idx}
                            src={img}
                            alt={`Slide ${idx}`}
                            className={`absolute h-[75vh] w-full rounded-xl drop-shadow-2xl object-contain transition-opacity duration-1000 ${idx === currentIndex ? 'opacity-100 z-10' : 'opacity-0 z-0'}`}
                            onError={() => setFailedImages(prev => ({ ...prev, [idx]: true }))}
                        />
                    )
                ))}
            </div>

            {/* Dots */}
            {images.length > 1 && (
                <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 flex gap-2 z-20">
                    {images.map((_, idx) => (
                        <div
                            key={idx}
                            className={`w-3 h-3 rounded-full transition-all border border-white/50 ${idx === currentIndex ? 'bg-white scale-125' : 'bg-white/20 hover:bg-white/40'}`}
                        />
                    ))}
                </div>
            )}
        </div>
    );
};
